// frontend/modules/template/stores/template-store.ts
import { create } from 'zustand';
import {
    Languages,
    NotificationPlatforms,
    TemplateContentType,
    TextTemplateDTO,
    MultiLanguageTemplateDTO
} from '@/shared/api/model';

interface TemplateState {
    templates: TextTemplateDTO[];
    filteredTemplates: TextTemplateDTO[];
    isLoading: boolean;
    error: string | null;
    searchTerm: string;
    selectedPlatform: NotificationPlatforms | null;

    // Active template state for editing
    activeTemplateName: string | null;
    activePlatform: NotificationPlatforms | null;
    activeContentType: TemplateContentType | null;
    activeContents: Record<Languages, string>;
    activeSubjects: Record<Languages, string>;
    isEditMode: boolean;

    // Actions
    setTemplates: (templates: TextTemplateDTO[]) => void;
    setFilteredTemplates: (templates: TextTemplateDTO[]) => void;
    setIsLoading: (isLoading: boolean) => void;
    setError: (error: string | null) => void;
    setSearchTerm: (term: string) => void;
    setSelectedPlatform: (platform: NotificationPlatforms | null) => void;
    setActiveTemplate: (name: string | null, platform: NotificationPlatforms | null, contentType: TemplateContentType | null) => void;
    setActiveContent: (language: Languages, content: string) => void;
    setActiveSubject: (language: Languages, subject: string) => void;
    setIsEditMode: (isEditMode: boolean) => void;
    resetActiveTemplate: () => void;
    filterTemplates: () => void;
}

export const useTemplateStore = create<TemplateState>((set, get) => ({
    templates: [],
    filteredTemplates: [],
    isLoading: false,
    error: null,
    searchTerm: '',
    selectedPlatform: null,

    activeTemplateName: null,
    activePlatform: null,
    activeContentType: null,
    activeContents: {
        [Languages.EN]: '',
        [Languages.SW]: ''
    },
    activeSubjects: {
        [Languages.EN]: '',
        [Languages.SW]: ''
    },
    isEditMode: false,

    setTemplates: (templates) => set({ templates, filteredTemplates: templates }),
    setFilteredTemplates: (filteredTemplates) => set({ filteredTemplates }),
    setIsLoading: (isLoading) => set({ isLoading }),
    setError: (error) => set({ error }),
    setSearchTerm: (searchTerm) => {
        set({ searchTerm });
        get().filterTemplates();
    },
    setSelectedPlatform: (selectedPlatform) => {
        set({ selectedPlatform });
        get().filterTemplates();
    },

    setActiveTemplate: (name, platform, contentType) => {
        set({
            activeTemplateName: name,
            activePlatform: platform,
            activeContentType: contentType,
            isEditMode: name !== null
        });

        // If setting an active template, find its contents
        if (name && platform) {
            const { templates } = get();
            const activeContents: Record<Languages, string> = {
                [Languages.EN]: '',
                [Languages.SW]: ''
            };
            const activeSubjects: Record<Languages, string> = {
                [Languages.EN]: '',
                [Languages.SW]: ''
            };

            // Find templates for both languages
            templates.forEach(template => {
                if (template.name === name && template.platform === platform) {
                    // @ts-ignore
                    activeContents[template.language] = template.content || '';
                    // @ts-ignore
                    activeSubjects[template.language] = template.subject || '';
                }
            });

            set({ activeContents, activeSubjects });
        }
    },

    setActiveContent: (language, content) => {
        set(state => ({
            activeContents: {
                ...state.activeContents,
                [language]: content
            }
        }));
    },

    setActiveSubject: (language, subject) => {
        set(state => ({
            activeSubjects: {
                ...state.activeSubjects,
                [language]: subject
            }
        }));
    },

    setIsEditMode: (isEditMode) => set({ isEditMode }),

    resetActiveTemplate: () => set({
        activeTemplateName: null,
        activePlatform: null,
        activeContentType: null,
        activeContents: {
            [Languages.EN]: '',
            [Languages.SW]: ''
        },
        activeSubjects: {
            [Languages.EN]: '',
            [Languages.SW]: ''
        },
        isEditMode: false
    }),

    filterTemplates: () => {
        const { templates, searchTerm, selectedPlatform } = get();

        let filtered = templates;

        // Filter by search term
        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            filtered = filtered.filter(template =>
                // @ts-ignore
                template.name.toLowerCase().includes(term)
            );
        }

        // Filter by platform
        if (selectedPlatform) {
            filtered = filtered.filter(template =>
                template.platform === selectedPlatform
            );
        }

        set({ filteredTemplates: filtered });
    }
}));